var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState } from 'react';
import { t } from 'i18next';
import CustomScrollbar from '../../components/CustomScrollbar';
import FooterActionsButtons from './FooterActionsButtons';
import ButtonComponent from '../../components/ButtonComponent';
import InputBox from '../../components/InputBox';
import RadioButton from '../../components/RadioButton';
import { DataContext } from '../../player/DataContext';
import globalStore from '../../store';
import { v4 as uuidv4 } from 'uuid';
import { NUMBER_LIMITS, MONEY_LIMITS, COIN_LIMITS, GLOBAL_TOTAL_LIMIT, NUMBER_DENOMINATIONS, MONEY_DENOMINATIONS, CURRENCY_IMAGES, DENOMINATION_KEYS, PIECE_DIMENSIONS, COIN_POPUP_DIMENSIONS as COIN_DIMENSIONS, MONEY_PIECE_DIMENSIONS, NUMBER_COLORS, } from '../../constants/nmpConstants';
import '../../styles/widgets/popup/numberAndMoneyPiecePopup.scss';
import { getImageSrc } from '../../helper/imageBlobCache';
var NumberAndMoneyPiecePopup = function (_a) {
    var onDone = _a.onDone, onCancel = _a.onCancel, configureItems = _a.configureItems;
    var getPositionForNewItem = useContext(DataContext).getPositionForNewItem;
    var btnConfig = globalStore.useStoreState(function (store) { return store.jsonDataStore; }).btnConfig;
    var _b = globalStore.useStoreState(function (store) { return store.playerStore; }), canvasWidth = _b.canvasWidth, canvasHeight = _b.canvasHeight, nmpAnimationEnabled = _b.nmpAnimationEnabled;
    var isConfigureMode = !!configureItems && configureItems.length > 0;
    var _c = useState(function () {
        var _a;
        if (configureItems && configureItems.length > 0) {
            var firstData = configureItems[0].data;
            return (_a = firstData.pieceType) !== null && _a !== void 0 ? _a : 'numberYellow';
        }
        return 'numberYellow';
    }), mode = _c[0], setMode = _c[1];
    var _d = useState(function () {
        var q = {
            ones: 0,
            tens: 0,
            hundreds: 0,
            dollar: 0,
            halfDollar: 0,
            quarter: 0,
            dime: 0,
            nickel: 0,
            penny: 0,
        };
        if (configureItems) {
            configureItems.forEach(function (item) {
                var data = item.data;
                if (data.denomination) {
                    q[data.denomination] = (q[data.denomination] || 0) + 1;
                }
            });
        }
        return q;
    }), quantities = _d[0], setQuantities = _d[1];
    var isNumberMode = mode === 'numberYellow' || mode === 'numberColored';
    var denominations = isNumberMode ? NUMBER_DENOMINATIONS : MONEY_DENOMINATIONS;
    var limits = isNumberMode ? NUMBER_LIMITS : mode === 'coins' ? COIN_LIMITS : MONEY_LIMITS;
    var totalQuantity = denominations.reduce(function (sum, d) { return sum + (quantities[d] || 0); }, 0);
    var anyDenomOverLimit = denominations.some(function (d) { var _a; return (quantities[d] || 0) > ((_a = limits[d]) !== null && _a !== void 0 ? _a : 20); });
    var isOverLimit = totalQuantity > GLOBAL_TOTAL_LIMIT || anyDenomOverLimit;
    var warningRef = useRef(null);
    var popupRef = useRef(null);
    // Popup-local aria-live region. The global announcer lives on document.body
    // which sits outside this popup's `aria-modal="true"` subtree, and several
    // screen readers (notably iOS VoiceOver) suppress live-region updates from
    // outside the active modal. A local element inside the modal guarantees the
    // announcement is heard.
    var srAnnouncerRef = useRef(null);
    var srAnnounceTimerRef = useRef(null);
    // Auto-focus the first interactive control on popup mount so keyboard / screen reader
    // users land inside the popup instead of the last-focused math tools list item.
    useEffect(function () {
        var focusTimer = setTimeout(function () {
            var root = popupRef.current;
            if (!root)
                return;
            var firstFocusable = root.querySelector('button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])');
            firstFocusable === null || firstFocusable === void 0 ? void 0 : firstFocusable.focus();
        }, 50);
        return function () { return clearTimeout(focusTimer); };
    }, []);
    var SCROLL_INTO_WARNING = false;
    useEffect(function () {
        if (SCROLL_INTO_WARNING && isOverLimit && warningRef.current) {
            warningRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [isOverLimit]);
    // Polite announcement of the new quantity so screen-reader users hear the
    // updated count immediately on each press / keystroke. The denomination is
    // included so the user knows which row changed even when focus is on a
    // shared control.
    var announceQuantity = function (denom, value) {
        var denomKey = DENOMINATION_KEYS[denom];
        var denomName = denomKey ? t(denomKey) : denom;
        var message = t('text.nmpQuantityChanged', { denom: denomName, count: value });
        var el = srAnnouncerRef.current;
        if (!el)
            return;
        // Clear-then-set so screen readers re-announce when the same denom is
        // adjusted to the same value back-to-back (e.g. + then − settles to
        // the original count).
        el.textContent = '';
        if (srAnnounceTimerRef.current)
            clearTimeout(srAnnounceTimerRef.current);
        srAnnounceTimerRef.current = setTimeout(function () {
            el.textContent = message;
        }, 50);
    };
    var handleIncrement = function (denom) {
        setQuantities(function (prev) {
            var _a;
            var next = (prev[denom] || 0) + 1;
            announceQuantity(denom, next);
            return __assign(__assign({}, prev), (_a = {}, _a[denom] = next, _a));
        });
    };
    var handleDecrement = function (denom) {
        setQuantities(function (prev) {
            var _a;
            var next = Math.max((prev[denom] || 0) - 1, 0);
            announceQuantity(denom, next);
            return __assign(__assign({}, prev), (_a = {}, _a[denom] = next, _a));
        });
    };
    // Real-time input handler: strip non-digits as the user types
    var handleQuantityInput = function (denom) { return function (_id, val, _e) {
        var digitsOnly = val.replace(/[^0-9]/g, '');
        var num = digitsOnly === '' ? 0 : parseInt(digitsOnly, 10);
        setQuantities(function (prev) {
            var _a;
            if (prev[denom] !== num)
                announceQuantity(denom, num);
            return __assign(__assign({}, prev), (_a = {}, _a[denom] = num, _a));
        });
    }; };
    // Blur / Enter handler: no clamping — warning text + disabled Done handle validation
    var handleQuantityChange = function (denom) { return function (_id, eventType, val) {
        if (eventType === 'blur' || eventType === 'keyUp') {
            var digitsOnly = String(val).replace(/[^0-9]/g, '');
            var num_1 = parseInt(digitsOnly, 10);
            if (isNaN(num_1) || num_1 < 0)
                num_1 = 0;
            // Skip if value didn't change (e.g. focus then immediate blur)
            if (num_1 === (quantities[denom] || 0))
                return;
            setQuantities(function (prev) {
                var _a;
                return (__assign(__assign({}, prev), (_a = {}, _a[denom] = num_1, _a)));
            });
        }
    }; };
    var getDimensions = function (denom) {
        var isNumber = mode === 'numberYellow' || mode === 'numberColored';
        return isNumber
            ? PIECE_DIMENSIONS[denom]
            : mode === 'coins'
                ? COIN_DIMENSIONS[denom]
                : MONEY_PIECE_DIMENSIONS[denom];
    };
    var handleConfigureDone = function () {
        if (!configureItems)
            return;
        // Zero-count Done in configure mode: teacher swapped material type
        // without entering counts. Treat as a pure right-toolbar swap — leave
        // the existing pieces on the canvas alone and route through the same
        // path as the create-mode zero-count Done (which uses the mode hint to
        // update the right drawer without touching `manipulative`).
        if (totalQuantity === 0) {
            onDone([], undefined, mode);
            return;
        }
        var isNumber = mode === 'numberYellow' || mode === 'numberColored';
        var updatedPieces = [];
        var newPieces = [];
        var deleteIds = [];
        var basePos = getPositionForNewItem();
        var offsetIndex = 0;
        denominations.forEach(function (denom) {
            var targetCount = quantities[denom] || 0;
            var dims = getDimensions(denom);
            var existingItems = configureItems.filter(function (item) {
                var data = item.data;
                return data.denomination === denom;
            });
            var existingCount = existingItems.length;
            var keepCount = Math.min(existingCount, targetCount);
            for (var i = 0; i < keepCount; i++) {
                var item = existingItems[i];
                var data = item.data;
                updatedPieces.push(__assign(__assign({}, item), { type: isNumber ? 'numberPiece' : 'moneyPiece', data: __assign(__assign({}, data), { type: isNumber ? 'numberPiece' : 'moneyPiece', pieceType: mode, animation: nmpAnimationEnabled, dimensions: __assign({}, dims), defaultDimensions: __assign({}, dims) }) }));
            }
            for (var i = keepCount; i < existingCount; i++) {
                var data = existingItems[i].data;
                deleteIds.push(data.id);
            }
            for (var i = 0; i < targetCount - existingCount; i++) {
                var offset = offsetIndex * 30;
                offsetIndex++;
                if (isNumber) {
                    var piece = {
                        id: "numberPiece_".concat(uuidv4().replace(/-/g, '')),
                        type: 'numberPiece',
                        pieceType: mode,
                        denomination: denom,
                        animation: nmpAnimationEnabled,
                        center: { x: basePos.x + offset, y: basePos.y + offset },
                        dimensions: __assign({}, dims),
                        defaultDimensions: __assign({}, dims),
                        scale: 1,
                        rotation: 0,
                        lock: { positioning: false, editing: false },
                    };
                    newPieces.push({
                        type: 'numberPiece',
                        widgetId: "numberPiece:".concat(uuidv4().replace(/-/g, '')),
                        data: piece,
                    });
                }
                else {
                    var piece = {
                        id: "moneyPiece_".concat(uuidv4().replace(/-/g, '')),
                        type: 'moneyPiece',
                        pieceType: mode,
                        denomination: denom,
                        animation: nmpAnimationEnabled,
                        center: { x: basePos.x + offset, y: basePos.y + offset },
                        dimensions: __assign({}, dims),
                        defaultDimensions: __assign({}, dims),
                        scale: 1,
                        rotation: 0,
                        isFlipped: false,
                        lock: { positioning: false, editing: false },
                    };
                    newPieces.push({
                        type: 'moneyPiece',
                        widgetId: "moneyPiece:".concat(uuidv4().replace(/-/g, '')),
                        data: piece,
                    });
                }
            }
        });
        // Delete any configureItems not processed (e.g. mode changed, old denominations don't match)
        var processedIds = new Set(__spreadArray(__spreadArray([], updatedPieces.map(function (p) { return p.data.id; }), true), deleteIds, true));
        configureItems.forEach(function (item) {
            var data = item.data;
            if (!processedIds.has(data.id)) {
                deleteIds.push(data.id);
            }
        });
        onDone(__spreadArray(__spreadArray([], updatedPieces, true), newPieces, true), deleteIds);
    };
    var handleDone = function () {
        if (isConfigureMode) {
            handleConfigureDone();
            return;
        }
        // Zero-count Done: teacher wants to land in the right-drawer config for
        // the selected material type without placing anything. Pass the mode as
        // a hint so the caller can resolve the correct rightConfig key.
        if (totalQuantity === 0) {
            onDone([], undefined, mode);
            return;
        }
        var pieces = [];
        var basePos = getPositionForNewItem();
        var GAP = 55;
        var CANVAS_MARGIN = 50;
        var flat = [];
        denominations.forEach(function (denom) {
            var count = quantities[denom] || 0;
            if (count === 0)
                return;
            var dims = isNumberMode
                ? PIECE_DIMENSIONS[denom]
                : mode === 'coins'
                    ? COIN_DIMENSIONS[denom]
                    : MONEY_PIECE_DIMENSIONS[denom];
            for (var i = 0; i < count; i++)
                flat.push({ denom: denom, dims: dims });
        });
        var totalWidth = flat.reduce(function (sum, e, i) { return sum + e.dims.width + (i > 0 ? GAP : 0); }, 0);
        var maxHeight = flat.reduce(function (h, e) { return Math.max(h, e.dims.height); }, 0);
        // Center the row on the current viewport, clamped inside the canvas.
        var gridStartX = Math.max(CANVAS_MARGIN, Math.min(basePos.x - totalWidth / 2, canvasWidth - totalWidth - CANVAS_MARGIN));
        var gridStartY = Math.max(CANVAS_MARGIN, Math.min(basePos.y - maxHeight / 2, canvasHeight - maxHeight - CANVAS_MARGIN));
        var cursorX = gridStartX;
        flat.forEach(function (entry) {
            var dims = entry.dims;
            var cx = cursorX + dims.width / 2;
            // Bottom-align: every piece's foot sits at gridStartY + maxHeight so
            // ones (55px) and tens/hundreds (550px) share the same baseline.
            var cy = gridStartY + maxHeight - dims.height / 2;
            cursorX += dims.width + GAP;
            if (isNumberMode) {
                var piece = {
                    id: "numberPiece_".concat(uuidv4().replace(/-/g, '')),
                    type: 'numberPiece',
                    pieceType: mode,
                    denomination: entry.denom,
                    animation: nmpAnimationEnabled,
                    center: { x: cx, y: cy },
                    dimensions: __assign({}, dims),
                    defaultDimensions: __assign({}, dims),
                    scale: 1,
                    rotation: 0,
                    lock: { positioning: false, editing: false },
                };
                pieces.push({
                    type: 'numberPiece',
                    widgetId: "numberPiece:".concat(uuidv4().replace(/-/g, '')),
                    data: piece,
                });
            }
            else {
                var piece = {
                    id: "moneyPiece_".concat(uuidv4().replace(/-/g, '')),
                    type: 'moneyPiece',
                    pieceType: mode,
                    denomination: entry.denom,
                    animation: nmpAnimationEnabled,
                    center: { x: cx, y: cy },
                    dimensions: __assign({}, dims),
                    defaultDimensions: __assign({}, dims),
                    scale: 1,
                    rotation: 0,
                    isFlipped: false,
                    lock: { positioning: false, editing: false },
                };
                pieces.push({
                    type: 'moneyPiece',
                    widgetId: "moneyPiece:".concat(uuidv4().replace(/-/g, '')),
                    data: piece,
                });
            }
        });
        onDone(pieces, undefined, mode);
    };
    var renderPreview = function () {
        if (isNumberMode) {
            var onesColor = NUMBER_COLORS[mode].ones;
            var tensColor_1 = NUMBER_COLORS[mode].tens;
            var hundredsColor_1 = NUMBER_COLORS[mode].hundreds;
            // Map the NUMBER_COLORS hex values back to translated color names so
            // screen readers can describe the preview blocks the same way they
            // describe coin / money-piece images.
            var HEX_TO_COLOR_KEY_1 = {
                '#FFFF00': 'text.colorYellow',
                '#58CC00': 'text.colorGreen',
                '#FF002B': 'text.colorRed',
            };
            var colorName_1 = function (hex) {
                var key = HEX_TO_COLOR_KEY_1[hex.toUpperCase()];
                return key ? t(key) : hex;
            };
            var previewLabel = function (denomKey, hex) {
                return t('text.numberPiecePreviewLabel', {
                    denom: t("text.".concat(denomKey)),
                    color: colorName_1(hex),
                });
            };
            return (_jsx("div", { className: "previewArea", children: _jsxs("div", { className: "previewPieces", children: [_jsx("div", { className: "previewHundreds", role: "img", "aria-label": previewLabel('hundreds', hundredsColor_1), children: Array.from({ length: 100 }).map(function (_, i) { return (_jsx("div", { className: "previewCell", style: { background: hundredsColor_1 }, "aria-hidden": "true" }, i)); }) }), _jsx("div", { className: "previewTens", role: "img", "aria-label": previewLabel('tens', tensColor_1), children: Array.from({ length: 10 }).map(function (_, i) { return (_jsx("div", { className: "previewCell", style: { background: tensColor_1 }, "aria-hidden": "true" }, i)); }) }), _jsx("div", { className: "previewOnes", role: "img", "aria-label": previewLabel('ones', onesColor), children: _jsx("div", { className: "previewCell", style: { background: onesColor }, "aria-hidden": "true" }) })] }) }));
        }
        else if (mode === 'coins') {
            var coinDenoms = [
                { key: 'dollar', label: '$1' },
                { key: 'halfDollar', label: '50¢' },
                { key: 'quarter', label: '25¢' },
                { key: 'dime', label: '10¢' },
                { key: 'nickel', label: '5¢' },
                { key: 'penny', label: '1¢' },
            ];
            return (_jsx("div", { className: "previewArea", children: _jsx("div", { className: "previewCoins", children: coinDenoms.map(function (_a) {
                        var key = _a.key, label = _a.label;
                        return (_jsx("div", { className: "coinPreview coinPreview--".concat(key === 'dollar' ? 'dollarBill' : key), children: _jsx("img", { src: getImageSrc(CURRENCY_IMAGES[key].front), alt: label, className: "coinPreviewImage", draggable: false }) }, key));
                    }) }) }));
        }
        else {
            // Money Pieces: grid blocks with overlaid real images
            var moneyGrids = [
                { key: 'dollar', cols: 10, rows: 10, label: '$1' },
                { key: 'halfDollar', cols: 5, rows: 10, label: '50¢' },
                { key: 'quarter', cols: 5, rows: 5, label: '25¢' },
                { key: 'dime', cols: 1, rows: 10, label: '10¢' },
                { key: 'nickel', cols: 1, rows: 5, label: '5¢' },
                { key: 'penny', cols: 1, rows: 1, label: '1¢' },
            ];
            return (_jsx("div", { className: "previewArea", children: _jsx("div", { className: "previewMoneyPieces", children: moneyGrids.map(function (_a) {
                        var key = _a.key, cols = _a.cols, rows = _a.rows, label = _a.label;
                        return (_jsxs("div", { className: "moneyGridWrapper", children: [_jsx("div", { className: "moneyGrid moneyGrid--".concat(key), style: {
                                        display: 'grid',
                                        gridTemplateColumns: "repeat(".concat(cols, ", var(--cell-size))"),
                                        gridTemplateRows: "repeat(".concat(rows, ", var(--cell-size))"),
                                    }, children: Array.from({ length: cols * rows }).map(function (_, i) { return (_jsx("div", { className: "previewCell moneyCell" }, i)); }) }), _jsx("div", { className: "moneyOverlay moneyOverlay--".concat(key), children: _jsx("img", { src: getImageSrc(CURRENCY_IMAGES[key].front), alt: label, className: "moneyOverlayImage", draggable: false }) })] }, key));
                    }) }) }));
        }
    };
    return (_jsxs("div", { className: "numberAndMoneyPiecePopup", ref: popupRef, children: [_jsx("div", { ref: srAnnouncerRef, "aria-live": "polite", "aria-atomic": "true", role: "status", style: {
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    padding: 0,
                    margin: -1,
                    overflow: 'hidden',
                    clip: 'rect(0, 0, 0, 0)',
                    whiteSpace: 'nowrap',
                    border: 0,
                } }), _jsxs("div", { className: "scrollbarHolder", children: [_jsx(CustomScrollbar, { direction: "vertical", children: _jsxs("div", { className: "scrollContentGroup", children: [renderPreview(), _jsxs("div", { style: { position: 'relative' }, children: [_jsx("hr", { className: "customHr", "aria-hidden": "true" }), _jsx("div", { className: "modeSelection", children: _jsx(RadioButton, { id: "pieceMode", options: [
                                                    t('text.numberPiecesYellow'),
                                                    t('text.numberPiecesColoured'),
                                                    t('text.nmpCoins'),
                                                    t('text.nmpMoneyPieces'),
                                                ], selected: ['numberYellow', 'numberColored', 'coins', 'money'].indexOf(mode), onChange: function (index) {
                                                    var modes = ['numberYellow', 'numberColored', 'coins', 'money'];
                                                    if (index >= 0) {
                                                        var newMode = modes[index];
                                                        var wasNumber = mode === 'numberYellow' || mode === 'numberColored';
                                                        var isNumber = newMode === 'numberYellow' || newMode === 'numberColored';
                                                        if (wasNumber !== isNumber) {
                                                            setQuantities({
                                                                ones: 0,
                                                                tens: 0,
                                                                hundreds: 0,
                                                                dollar: 0,
                                                                halfDollar: 0,
                                                                quarter: 0,
                                                                dime: 0,
                                                                nickel: 0,
                                                                penny: 0,
                                                            });
                                                        }
                                                        setMode(newMode);
                                                    }
                                                } }) }), _jsx("hr", { className: "sectionDivider", "aria-hidden": "true" }), _jsx("div", { className: "quantityWrapper", children: _jsx("div", { className: "quantityControls", children: denominations.map(function (denom) { return (_jsxs("div", { className: "quantityRow quantityRow--".concat(denom), children: [_jsxs("div", { className: "denomLabel", children: [t(DENOMINATION_KEYS[denom]), ":"] }), _jsxs("div", { className: "incDecGroup", children: [_jsx(ButtonComponent, { type: "imageType4", defaultCSS: "btn4", imgSrc: btnConfig.minus2.imgSrc, clickHandler: function () { return handleDecrement(denom); }, disabled: (quantities[denom] || 0) <= 0, tabIndex: (quantities[denom] || 0) <= 0 ? -1 : 0, ariaLabel: "".concat(t('text.decrease'), " ").concat(t(DENOMINATION_KEYS[denom]), " ").concat(t('text.count')) }), _jsx(InputBox, { id: "qty_".concat(denom), value: String(quantities[denom] || 0), inputControlledFromOutside: true, maxLength: 3, onInput: handleQuantityInput(denom), onChange: handleQuantityChange(denom), ariaLabel: "".concat(t(DENOMINATION_KEYS[denom]), " ").concat(t('text.count')), defaultCSS: "quantityInput" }), _jsx(ButtonComponent, { type: "imageType4", defaultCSS: "btn4", imgSrc: btnConfig.plus2.imgSrc, clickHandler: function () { return handleIncrement(denom); }, ariaLabel: "".concat(t('text.increase'), " ").concat(t(DENOMINATION_KEYS[denom]), " ").concat(t('text.count')) })] })] }, denom)); }) }) }), isOverLimit && (_jsxs("div", { className: "limitWarning", ref: warningRef, children: [totalQuantity > GLOBAL_TOTAL_LIMIT &&
                                                    t('text.maxPiecesAllowed', { limit: GLOBAL_TOTAL_LIMIT }) + ' ', anyDenomOverLimit &&
                                                    t('text.exceededMaxLimit', {
                                                        denoms: denominations
                                                            .filter(function (d) {
                                                            var _a;
                                                            return (quantities[d] || 0) >
                                                                ((_a = limits[d]) !== null && _a !== void 0 ? _a : 20);
                                                        })
                                                            .map(function (d) { var _a; return "".concat(t(DENOMINATION_KEYS[d]), " (").concat(t('text.max'), " ").concat((_a = limits[d]) !== null && _a !== void 0 ? _a : 20, ")"); })
                                                            .join(', '),
                                                    })] }))] })] }) }), _jsx(FooterActionsButtons, { disableDoneBtn: isOverLimit, onActionBtnClick: function (type) {
                            if (type === 'done') {
                                handleDone();
                            }
                            else {
                                onCancel();
                            }
                        } })] })] }));
};
export default NumberAndMoneyPiecePopup;
